/**
 * Hover flip-through for album cards (setting: Album hover preview): while the pointer rests on a card that holds several
 * pictures, the card steps through them. No DOM here, just the rules and a small shared cache for Erome albums.
 */

export const MAX_PREVIEW_FRAMES = 8
export const FLIP_INTERVAL_MS = 700
export const HOVER_DWELL_MS = 450

const VIDEO_URL = /\.(webm|mp4|mov|avi|mkv|flv|wmv|m4v|3gp|ogv)(\?|$)/i

export interface AlbumPostLike {
  site?: string
  type?: string
  source?: string | null
  albumImages?: string[] | null
}

/** Pictures only (a video file cannot be shown as a still), one of each, in order. */
export function pictureUrls(urls: readonly string[] | null | undefined): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const url of urls ?? []) {
    if (!url || VIDEO_URL.test(url) || seen.has(url)) continue
    seen.add(url)
    out.push(url)
  }
  return out
}

/** At most `max` frames, spread evenly over the list so a long album still shows its beginning, middle and end. */
export function sampleFrames(urls: string[], max: number = MAX_PREVIEW_FRAMES): string[] {
  if (urls.length <= max) return urls
  const out: string[] = []
  for (let i = 0; i < max; i++) out.push(urls[Math.round((i * (urls.length - 1)) / (max - 1))])
  return out
}

/** The pictures a card already carries (Kemono's thumbnails, Erome albums that were opened), or [] when it carries none. */
export function framesFromPost(post: AlbumPostLike): string[] {
  return sampleFrames(pictureUrls(post.albumImages))
}

/** An Erome album whose pictures can be fetched (once) when someone hovers it: it came back from search as a bare cover, or its
 * own pictures are too few to flip through (an album of videos). */
export function canFetchEromeFrames(post: AlbumPostLike): boolean {
  return post.site === "erome" && post.type === "album" && !!post.source
}

/** The poster pictures of the videos on an Erome album page (each video appears twice in the page, so repeats are dropped). */
export function extractVideoPosters(html: string): string[] {
  const out: string[] = []
  const tag = /<video\b[^>]*>/gi
  let m: RegExpExecArray | null
  while ((m = tag.exec(html)) !== null) {
    const poster = m[0].match(/\bposter=["'](https?:\/\/[^"']+)["']/i)?.[1]
    if (poster && !out.includes(poster)) out.push(poster)
  }
  return out
}

/** Which poster belongs to a video: Erome names them alike ("abc123_720p.mp4" and "abc123.jpg"). Falls back to the same position. */
export function posterForVideo(videoUrl: string, posters: string[], index: number): string | undefined {
  const stem = (url: string) => url.split("?")[0].split("/").pop()?.replace(/\.[a-z0-9]+$/i, "").replace(/_\d+p$/i, "")
  const wanted = stem(videoUrl)
  return posters.find((p) => stem(p) === wanted) ?? posters[index]
}

// ── Erome fetches ────────────────────────────────────────────────────────────────────────────────────────────
// Erome throttles quickly, so hover fetches go one at a time through this queue, are cached, and are shared when the same
// album is hovered twice. The server side (/api/booru/erome/gallery) adds its own governor and cache on top.

const CACHE_LIMIT = 150
const cache = new Map<string, string[]>()
const inflight = new Map<string, Promise<string[]>>()
let queue: Promise<unknown> = Promise.resolve()

export function getCachedEromeFrames(albumUrl: string): string[] | undefined {
  return cache.get(albumUrl)
}

export function fetchEromeFrames(albumUrl: string): Promise<string[]> {
  const hit = cache.get(albumUrl)
  if (hit) return Promise.resolve(hit)
  const pending = inflight.get(albumUrl)
  if (pending) return pending

  const job = queue
    .catch(() => undefined)
    .then(async () => {
      try {
        const res = await fetch(`/api/booru/erome/gallery?url=${encodeURIComponent(albumUrl)}`)
        if (!res.ok) return [] as string[]
        const data = (await res.json()) as { pages?: Array<{ url: string; type: string; poster?: string }> }
        // a photo shows as itself, a video as its poster picture
        const frames = sampleFrames(pictureUrls((data.pages ?? []).map((p) => (p.type === "image" ? p.url : p.poster ?? ""))))
        if (cache.size >= CACHE_LIMIT) cache.delete(cache.keys().next().value as string)
        cache.set(albumUrl, frames)
        return frames
      } catch {
        return [] as string[]
      }
    })
    .finally(() => inflight.delete(albumUrl))
  queue = job
  inflight.set(albumUrl, job)
  return job
}
